import { Card, Chip, Divider, Stack, Typography } from '@mui/material';
import { computeOccupancy, HealthCenter } from '@silbo/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConfigContext } from '../../../contexts/ConfigContext/useConfigContext';
import useLiveQuery from '../../../hooks/useLiveQuery';
import PageContainer from '../components/PageContainer';
import StatisticIcon from '../components/StatisticIcon';
import StatisticText from '../components/StatisticText';
import { getUnitsQuery, getUnitsQueryOptions } from '../queries';
import { getOccupancyLevelColor } from '../utils/occupancy-levels-colors';

interface StatisticsPageProps {
  userUnitIds: string[];
  healthCenter: HealthCenter | undefined;
}

const StatisticsPage = ({ userUnitIds, healthCenter }: StatisticsPageProps) => {
  const { t } = useTranslation('bed-management');
  const { config } = useConfigContext();

  const { data: units } = useSuspenseQuery(getUnitsQueryOptions('healthCenter', healthCenter, userUnitIds));
  useLiveQuery(getUnitsQuery('healthCenter', healthCenter, userUnitIds));

  if (!config) {
    return null;
  }

  return (
    <PageContainer title={t('statistics.title.unit', { count: units.length })}>
      <Stack spacing={8}>
        {units.map((unit) => {
          if (!unit.occupancy || !unit.occupancy.currentOccupancy) {
            return null;
          }

          const currentOccupancy = unit.occupancy.currentOccupancy;
          const endOfDayOccupancy = computeOccupancy([unit.occupancy]);
          const currentlyBusy = currentOccupancy.open - currentOccupancy.available - currentOccupancy.booked;

          return (
            <Stack key={unit.id} spacing={3}>
              <Typography fontWeight={600}>{unit.name}</Typography>
              <Stack direction="row" alignItems="center" gap={4}>
                <Card sx={{ padding: 4, flex: 0.5 }} elevation={1}>
                  <Stack spacing={3}>
                    <Typography>{t('statistics.currently')}</Typography>
                    <Stack spacing={4} divider={<Divider />}>
                      <StatisticText>
                        {currentlyBusy}{' '}
                        <Typography variant="body2" component="span" color="secondary">
                          / {unit.occupancy.currentOccupancy?.open}
                        </Typography>
                      </StatisticText>
                      <Stack direction="row" alignItems="center" gap={2}>
                        <StatisticText>{Math.max(0, unit.occupancy.currentOccupancy?.available)}</StatisticText>
                        <Chip
                          variant="light"
                          color="success"
                          label={t('statistics.available', {
                            count: Math.max(0, unit.occupancy.currentOccupancy?.available),
                          })}
                        />
                      </Stack>
                      <Stack direction="row" alignItems="center" gap={2}>
                        <StatisticIcon config={config} rate={currentOccupancy.rate} />
                        <StatisticText color={getOccupancyLevelColor(currentOccupancy.rate, config)?.main}>
                          {currentOccupancy.rate}%
                        </StatisticText>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
                <Card sx={{ padding: 4, flex: 0.5 }} elevation={1}>
                  <Stack spacing={3}>
                    <Typography>{t('statistics.endOfDay')}</Typography>
                    <Stack spacing={4} divider={<Divider />}>
                      <StatisticText>
                        {endOfDayOccupancy.occupied}{' '}
                        <Typography variant="body2" component="span" color="secondary">
                          / {endOfDayOccupancy.total}
                        </Typography>
                      </StatisticText>
                      <Stack direction="row" alignItems="center" gap={2}>
                        <StatisticText>{Math.max(0, endOfDayOccupancy.available)}</StatisticText>
                        <Chip
                          variant="light"
                          color="success"
                          label={t('statistics.available', { count: Math.max(0, endOfDayOccupancy.available) })}
                        />
                      </Stack>
                      <Stack direction="row" alignItems="center" gap={2}>
                        <StatisticIcon config={config} rate={endOfDayOccupancy.rate} />
                        <StatisticText color={getOccupancyLevelColor(endOfDayOccupancy.rate, config)?.main}>
                          {endOfDayOccupancy.rate}%
                        </StatisticText>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </PageContainer>
  );
};

export default StatisticsPage;
